/**
 * Лист снизу закрывается свайпом вниз (16.10.2026).
 *
 * Внутри листа бывает прокрутка, и она главнее: свайп вниз по содержимому
 * сначала прокручивает его к началу. Лист поедет за пальцем, только когда
 * прокручивать уже некуда, — или если тянут за полоску сверху и заголовок:
 * они закрывают всегда. Барабаны календаря и поля ввода лист не тянут.
 *
 * Протянули больше трети высоты или смахнули резко — лист уезжает вниз и
 * закрывается. Нет — мягко возвращается на место. Затемнение гаснет вслед
 * за листом.
 */
import { useEffect, useRef, type RefObject } from 'react';

/** С какого сдвига палец считается свайпом, а не касанием. */
const SLOP = 8;
/** Доля высоты листа, после которой отпущенный лист закрывается. */
const FAR = 1 / 3;
/** Скорость смахивания, точек в миллисекунду. */
const FLICK = 0.5;

/** Тянуть лист отсюда нельзя: барабаны листаются сами, в поле с курсором его двигают. */
const OWN = '.dpk-wheel, input:focus, textarea:focus, input[type=range], [contenteditable], [data-noswipe]';
/** Отсюда лист тянется всегда, даже если содержимое прокручено. */
const GRIP = '.grab, .sheethead, .opbar';

const calm = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Прокручиваемые блоки от пальца до тела листа: свайп сначала их. */
const scrollers = (from: Element | null, body: HTMLElement): HTMLElement[] => {
  const out: HTMLElement[] = [];
  for (let el = from; el; el = el.parentElement) {
    if (el instanceof HTMLElement && el.scrollHeight > el.clientHeight + 1) {
      const y = getComputedStyle(el).overflowY;
      if (y === 'auto' || y === 'scroll') out.push(el);
    }
    if (el === body) break;
  }
  return out;
};

/**
 * `enabled` — можно ли закрыть свайпом: на втором шаге листа нельзя, там нет
 * и крестика. `onSwiped` — лист уже уехал сам, прощальная копия не нужна.
 */
export function useSwipeClose(
  root: RefObject<HTMLDivElement>, body: RefObject<HTMLDivElement>,
  enabled: boolean, onClose: () => void, onSwiped: (gone: boolean) => void,
): void {
  const latest = useRef({ enabled, onClose, onSwiped });
  latest.current = { enabled, onClose, onSwiped };
  useEffect(() => {
    const sheet = root.current;
    const box = body.current;
    const bg = sheet?.querySelector<HTMLElement>(':scope > .bg') ?? null;
    if (!sheet || !box) return;
    let mode: 'idle' | 'drag' | 'scroll' | 'off' = 'off';
    let x0 = 0; let y0 = 0; let from = 0; let shift = 0;
    let grip = false; let lists: HTMLElement[] = [];
    let last = { y: 0, t: 0 }; let speed = 0;
    let dragged = false;

    const atTop = () => lists.every((el) => el.scrollTop <= 0);
    const paint = (dy: number, how = 'none') => {
      box.style.transition = how;
      box.style.transform = dy > 0 ? `translateY(${dy}px)` : '';
      if (bg) {
        bg.style.transition = how === 'none' ? 'none' : how.replace('transform', 'opacity');
        bg.style.opacity = dy > 0 ? String(Math.max(0, 1 - dy / Math.max(1, box.offsetHeight))) : '';
      }
    };
    const reset = () => {
      box.style.transition = ''; box.style.transform = '';
      if (bg) { bg.style.transition = ''; bg.style.opacity = ''; }
    };

    const start = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length > 1 || !latest.current.enabled) { mode = 'off'; return; }
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest(OWN)) { mode = 'off'; return; }
      grip = !!target?.closest(GRIP);
      lists = scrollers(target, box);
      x0 = t.clientX; y0 = t.clientY; shift = 0; speed = 0; dragged = false;
      last = { y: t.clientY, t: e.timeStamp };
      mode = 'idle';
    };

    const move = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || mode === 'off') return;
      const dx = t.clientX - x0; const dy = t.clientY - y0;
      if (mode === 'idle') {
        if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
        if (Math.abs(dx) > Math.abs(dy)) { mode = 'off'; return; }
        if (dy > 0 && (grip || atTop())) { mode = 'drag'; from = t.clientY; }
        else mode = 'scroll';
      }
      // Содержимое докрутили до начала, а палец всё ведёт вниз — дальше едет лист.
      if (mode === 'scroll' && e.cancelable && t.clientY > last.y && atTop()) { mode = 'drag'; from = t.clientY; }
      // Анимация появления главнее сдвига: пока она идёт, лист за пальцем не поехал бы.
      if (mode === 'drag' && box.style.animation !== 'none') box.style.animation = 'none';
      if (mode === 'drag') {
        if (e.cancelable) e.preventDefault();
        shift = Math.max(0, t.clientY - from);
        dragged = dragged || shift > SLOP;
        const dt = e.timeStamp - last.t;
        if (dt > 0) speed = 0.8 * ((t.clientY - last.y) / dt) + 0.2 * speed;
        paint(shift);
      }
      last = { y: t.clientY, t: e.timeStamp };
    };

    const end = () => {
      if (mode !== 'drag') { mode = 'off'; return; }
      mode = 'off';
      // Клик после касания приходит сразу; позже — уже чужой, его не глотаем.
      if (dragged) window.setTimeout(() => { dragged = false; }, 400);
      const height = box.offsetHeight || 1;
      if (shift > height * FAR || (speed > FLICK && shift > SLOP * 2)) {
        const { onClose: close, onSwiped } = latest.current;
        const leave = () => {
          onSwiped(true); close();
          // Лист не закрылся (переспросил, не дал) — возвращается на место, а не висит за краем.
          window.setTimeout(() => { if (box.isConnected) { onSwiped(false); reset(); } }, 80);
        };
        if (calm()) { leave(); return; }
        paint(height + 40, 'transform .22s cubic-bezier(.4,0,1,1)');
        window.setTimeout(leave, 220);
      } else if (shift > 0) {
        if (calm()) { reset(); return; }
        paint(0, 'transform .28s cubic-bezier(.2,.8,.2,1)');
        window.setTimeout(reset, 300);
      } else reset();
    };

    // Тянули лист — отпущенный палец не нажимает кнопку, на которой оказался.
    const swallow = (e: MouseEvent) => {
      if (!dragged) return;
      dragged = false;
      e.stopPropagation(); e.preventDefault();
    };

    box.addEventListener('touchstart', start, { passive: true });
    box.addEventListener('touchmove', move, { passive: false });
    box.addEventListener('touchend', end);
    box.addEventListener('touchcancel', end);
    box.addEventListener('click', swallow, true);
    return () => {
      box.removeEventListener('touchstart', start);
      box.removeEventListener('touchmove', move);
      box.removeEventListener('touchend', end);
      box.removeEventListener('touchcancel', end);
      box.removeEventListener('click', swallow, true);
    };
  }, [root, body]);
}
